import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as CoinIcon } from 'src/assets/icons/bonus-game/tether-coin.svg';
import { ReactComponent as SeasonsIcon } from 'src/assets/icons/bonus-game/seasons.svg';
import { ReactComponent as MedalGold } from 'src/assets/icons/bonus-game/season-silver-medal.svg';
import { ReactComponent as MedalSilver } from 'src/assets/icons/bonus-game/season-silver-medal.svg';
import { ReactComponent as MedalBronze } from 'src/assets/icons/bonus-game/season-bronze-medal.svg';
import { ReactComponent as MedalRegular } from 'src/assets/icons/bonus-game/season-regular-medal.svg';
import discordBlur from 'src/assets/img/bonus-game/discord-blur.webp';
import currentSeasonBonus from 'src/assets/img/bonus-game/current-season-reward.webp';
import prevSeasonBonus from 'src/assets/img/bonus-game/previous-season-reward.webp';
import { MobileHeader } from '../../components/Header';
import { PageWrapper } from '../../ui/common';
import { useEffect, useState } from 'react';
import { bonusGameApi } from 'src/api/bonus-game';
import Loader from 'src/pages/Education/components/Loader';
import { VerticalView } from 'src/components/FlexViews';

const SeasonPeriod = styled.div`
  text-align: center;
  color: #bfff38;
  font-size: 14px;
  margin-bottom: 20px;
`;

const AwardCard = styled.img`
  margin-bottom: 20px;
`;

const AwardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
`;

const LeaderboardItem = styled.div<{ place: number }>`
  background-color: #1a222c;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MedalWrapper = styled.div`
  position: relative;
  margin-right: 10px;
`;

const MedalPosition = styled.p`
  position: absolute;
  top: 50%;
  right: 50%;
  transform: translate(50%, -50%);
  font-family: Public-Sans, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 12px;
  color: rgba(0, 0, 0, 0.8);
  text-align: center;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const UserName = styled.div`
  color: #ffffff;
  font-weight: 500;
  font-size: 12px;
  width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserScore = styled.div<{ place?: number }>`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ffffff;
  position: relative;
`;

const DiscordSection = styled.div`
  overflow: hidden;
  background-color: #ffffff14;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 30px 0;
`;

const DiscordInfo = styled.div`
  flex: 1;
  gap: 8px;
  padding: 20px;
`;

const DiscordTitle = styled.div`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
`;

const DiscordText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 500;
`;

const DiscordIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Ellipse = styled.div`
  position: absolute;
  right: -45px;
  top: 1.174px;
  width: 154px;
  height: 49px;
  background: #c8f744;
  filter: blur(39.37645721435547px);
  opacity: 0.3;
`;

const FirstPositionText = styled.p`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translate(-50%, -12px);
  color: #fff;
  text-overflow: ellipsis;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const medalIcons = [
  <MedalGold key={'gold'} />,
  <MedalSilver key={'silver'} />,
  <MedalBronze key={'bronze'} />,
];

const getMedalIcon = (position: number) => {
  if (position > 3) {
    return <MedalRegular />;
  }
  return medalIcons[position - 1];
};

interface LeaderboardUser {
  id?: string;
  name?: string;
  rating?: number;
  position?: number;
}

const seasonRewardsData = [
  { position: 2, rating: 100 },
  { position: 3, rating: 50 },
  { position: 4, rating: 30 },
  { position: 5, rating: 15 },
];

function formatDate(dateString: any) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const SeasonLeaderboard = () => {
  const navigate = useNavigate();
  const [seasonPeriodLocal, setSeasonPeriodLocal] = useState('');
  const [seasonRewards, setSeasonRewards] =
    useState<{ position: number; rating: number; userName?: string }[]>(
      seasonRewardsData,
    );
  const [firstPosTreeName, setFirstPosTreeName] = useState('');
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const { seasonPeriod } = location.state || {};

  const { id: seasonId } = useParams();

  const seasonTitle = `Season ${seasonId || '2'}`;

  const handleGoToAllSeasons = () => {
    navigate('/bonus-game/seasons');
  };

  useEffect(() => {
    (async () => {
      if (seasonPeriod) {
        setSeasonPeriodLocal(seasonPeriod);
        return;
      }

      const seasonsRes = (await bonusGameApi.getSeasons()).result;
      const start = seasonsRes[1].startDate;
      const end = seasonsRes[1].endDate;
      const result = `${formatDate(start)} - ${formatDate(end)}`;
      setSeasonPeriodLocal(result);
    })();
  }, [seasonPeriod]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (seasonId === '1') {
          const leaderBoardRes = await bonusGameApi.getLeaderBoard({
            pagination: { take: 6, skip: 0 },
            seasonId: 1,
          });
          setFirstPosTreeName(leaderBoardRes.result[0].bonusTreeName);
          const mergedSeasonRewards = seasonRewards.map((reward, index) => ({
            ...reward,
            userName: leaderBoardRes.result[index + 1].bonusTreeName,
          }));

          mergedSeasonRewards.push({
            position: 6,
            rating: 10,
            userName: leaderBoardRes.result.at(-1)?.bonusTreeName ?? '',
          });
          setSeasonRewards(mergedSeasonRewards);
        }
      } catch (e) {
        console.log('e', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [seasonId]);

  if (loading) return <Loader />;

  return (
    <PageWrapper>
      <MobileHeader
        title={seasonTitle}
        headerRight={
          <button onClick={handleGoToAllSeasons}>
            <SeasonsIcon />
          </button>
        }
      />

      <SeasonPeriod>
        {seasonId === '1' ? seasonPeriodLocal : '15 April 2025 - 15 May 2025'}
      </SeasonPeriod>
      <VerticalView style={{ position: 'relative' }}>
        <AwardCard
          style={{ width: '100%' }}
          src={seasonId === '1' ? prevSeasonBonus : currentSeasonBonus}
          alt="Season Leader Board"
        />
        <FirstPositionText>{firstPosTreeName}</FirstPositionText>
      </VerticalView>

      {seasonRewards.map((user, index) => (
        <LeaderboardItem key={index} place={index + 2}>
          <UserInfo>
            <MedalWrapper>
              {getMedalIcon(index + 2)}
              <MedalPosition
                style={{
                  color: 'rgba(0, 0, 0, 0.8)',
                }}>
                {index + 2}
              </MedalPosition>
            </MedalWrapper>
            <UserName>{user.userName || 'Place'}</UserName>
          </UserInfo>
          <UserScore>
            <span style={{ fontSize: '16px', fontWeight: '700' }}>
              {user.rating}
            </span>
            <CoinIcon width={16} height={16} />
            {index === 0 && <Ellipse />}
            {index === 1 && <Ellipse style={{ background: '#B0823E' }} />}
          </UserScore>
        </LeaderboardItem>
      ))}
      {seasonId !== '1' && (
        <LeaderboardItem place={6}>
          <UserInfo>
            <MedalWrapper>
              <MedalRegular />
              <MedalPosition
                style={{
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  color: 'rgba(0, 0, 0, 0.8)',
                }}>
                6-10
              </MedalPosition>
            </MedalWrapper>
            <UserName>Places</UserName>
          </UserInfo>
          <UserScore>
            <span style={{ fontSize: '16px', fontWeight: '700' }}>10</span>
            <CoinIcon width={16} height={16} />
          </UserScore>
        </LeaderboardItem>
      )}

      <DiscordSection>
        <DiscordInfo>
          <DiscordTitle>Top 200 members</DiscordTitle>
          <DiscordText>
            Ambassador role on Haqabi{' '}
            <a
              style={{ color: '#5865F2' }}
              href="https://discord.gg/kXeefuKPmQ">
              Discord
            </a>
          </DiscordText>
        </DiscordInfo>
        <DiscordIconWrapper>
          <img
            style={{ objectPosition: '-35px 5px', objectFit: 'cover' }}
            src={discordBlur}
            width={'500%'}
            height={'88px'}
          />
        </DiscordIconWrapper>
      </DiscordSection>
    </PageWrapper>
  );
};
